import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, CreditCard, Building, Clock, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Seo } from "../components/Seo";

const ATM_PROVIDERS = [
  { name: "KCB Bank", available: true },
  { name: "Equity Bank", available: true },
  { name: "DTB Bank", available: true },
];

export default function ATMPage() {
  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="ATM Services"
        path="/atm"
        description="24/7 ATM banking at Nyali Plaza, Mombasa. Access KCB, Equity and DTB Bank ATMs in a secure, CCTV-monitored area on the ground floor near the main entrance."
      />
      {/* Header */}
      <header className="sticky top-0 z-50 glass glass-dark border-b border-border/50">
        <div className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-3 flex items-center gap-3">
          <Link to="/" data-testid="back-to-home">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading font-bold text-lg leading-tight text-foreground">ATM Services</h1>
            <p className="text-xs text-muted-foreground">Bank ATMs at Nyali Plaza</p>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-green-700 to-green-900 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent)]" />
        </div>
        <div className="relative max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-3 tracking-tight">
              ATM Room
            </h2>
            <p className="text-green-100 text-base md:text-lg max-w-xl mx-auto">
              Convenient 24/7 banking services from multiple banks
            </p>
          </motion.div>
        </div>
      </section>

      <main className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-8 md:py-12">
        {/* Location */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="bg-card rounded-2xl border border-border/50 p-6 mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <MapPin className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground">Location</h3>
              <p className="text-muted-foreground">Ground Floor, Near Main Entrance</p>
            </div>
          </div>
        </motion.div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="bg-card rounded-2xl border border-border/50 p-6 text-center"
          >
            <Clock className="w-8 h-8 text-primary mx-auto mb-3" />
            <h4 className="font-heading font-semibold text-foreground mb-1">24/7 Access</h4>
            <p className="text-sm text-muted-foreground">Available round the clock</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-card rounded-2xl border border-border/50 p-6 text-center"
          >
            <Shield className="w-8 h-8 text-primary mx-auto mb-3" />
            <h4 className="font-heading font-semibold text-foreground mb-1">Secure Area</h4>
            <p className="text-sm text-muted-foreground">CCTV monitored</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="bg-card rounded-2xl border border-border/50 p-6 text-center"
          >
            <Building className="w-8 h-8 text-primary mx-auto mb-3" />
            <h4 className="font-heading font-semibold text-foreground mb-1">Multiple Banks</h4>
            <p className="text-sm text-muted-foreground">Choose your bank</p>
          </motion.div>
        </div>

        {/* ATM List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <h3 className="font-heading text-xl font-bold text-foreground mb-4">Available ATMs</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ATM_PROVIDERS.map((atm, index) => (
              <div
                key={atm.name}
                className="bg-card rounded-2xl border border-border/50 p-4 text-center hover:border-primary/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-3">
                  <CreditCard className="w-6 h-6 text-green-700 dark:text-green-400" />
                </div>
                <h4 className="font-medium text-foreground text-sm">{atm.name}</h4>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium mt-2">
                  Available
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mt-8 bg-accent/50 rounded-2xl p-6 text-center"
        >
          <p className="text-muted-foreground text-sm">
            For card issues or transaction problems, please contact your respective bank's customer service.
          </p>
        </motion.div>

        {/* Back */}
        <div className="text-center mt-8">
          <Link to="/" data-testid="back-home">
            <Button variant="outline" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
